
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToParentElement } from '@dnd-kit/modifiers'
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { MediaPreviewPopup } from 'components/Composer/BulkView/components/MediaPreviewPopup'
import {
  ACTION_RESET_CONTEXT_FILES,
  ComposerMediaContext,
  FileUploadState
} from 'components/ComposerMediaContext'
import { FileInfo } from 'components/FileInfo'
import { UploadHandle } from 'components/FileUpload/FileUpload'
import { PhotoEditorDialog } from 'components/PhotoEditor'
import { VideoEditorDialog } from 'components/VideoEditorDialog'
import { useDeleteFileQueue } from 'hooks/useDeleteFileQueue'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import {
  addComposerVideo,
  removeComposerImages,
  removeComposerVideo,
  reorderComposerImages,
  updateComposerImage,
  updateComposerVideo
} from 'services/compose'
import { composerImagesSelector, composerVideoSelector, composerVideoThumbnailSelector } from 'services/compose/selectors'
import { MediaItemState } from 'services/compose/state'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { reorder, sortByKeyAscending } from 'utils/sort/order'
import { ExternalImageGridItem } from './ExternalImageGridItem'
import { FileGridItem, SortableFileGridItem } from './FileGridItem'
import styles from './MediaPreview.pcss'
import { UploaderVideoPreview } from './UploaderVideoPreview'

// NOTE: dnd-kit's own DragEndEvent type cannot be imported here — every type-only export of
// @dnd-kit/core resolves to a namespace in this project (TS2709).
type DragEndPayload = {
  active: { id: string },
  over: { id: string } | null
}

type VideoEditState = {
  id?: string
  videoUrl?: string
  editorOpen: boolean
}

export function MediaPreview({ className }: { className?: string }) {
  const intl = useIntl()
  const storeDispatch = useDispatch<StoreThunkDispatch>()
  const { dispatch: contextDispatch, files } = useContext(ComposerMediaContext)
  const images = useSelector(composerImagesSelector)
  const composerVideo = useSelector(composerVideoSelector)
  const videoFile = composerVideo ? files[composerVideo.id] : undefined
  // NOTE: the store holds the video metadata, the media context only the local File and its upload
  // handle — so the store spread goes last and stays the source of truth for `url`. Memoized to keep
  // a stable identity for the callbacks that depend on it.
  const video = useMemo(
    () => (composerVideo && (composerVideo.url || videoFile?.file) ? { ...videoFile, ...composerVideo } : null),
    [composerVideo, videoFile]
  )
  const videoUrl = video?.url
  const videoThumbnailUrl = useSelector(composerVideoThumbnailSelector)

  const [photoEditorImage, setPhotoEditorImage] = useState<FileUploadState & MediaItemState | null>(null)
  const [videoEdit, setVideoEdit] = useState<VideoEditState>({ editorOpen: false })
  const [filePreview, setFilePreview] = useState<{ url: string, selector: string, type: 'image' | 'video' } | null>(null)

  const deleteUpload = useDeleteFileQueue()
  const withFiles = Boolean(video?.id) || Object.keys(images).length > 0
  const order = useMemo(() => Object.values(images).sort(sortByKeyAscending('order')).map(f => f.id), [images])

  // EXPL: the grid tiles are memoized, so every callback handed to them has to keep a stable
  // identity. The media they read changes on every upload tick — reading it through a ref keeps it
  // out of the dependency lists while the handlers still see the state as of the last commit.
  // NOTE: seeded lazily — passing the object to useRef would build and throw one away on every render.
  const latest = useRef<{
    images: typeof images,
    files: typeof files,
    video: typeof video,
    order: typeof order
  }>(null!)
  if (!latest.current) {
    latest.current = { images, files, video, order }
  }
  useEffect(() => {
    latest.current = { images, files, video, order }
  })

  const onRemoveFile = useCallback((_fileName: string, fileId: string) => {
    const { images: currentImages, files: currentFiles, video: currentVideo } = latest.current
    let fileUrl = ''
    if (fileId === currentVideo?.id) {
      storeDispatch(removeComposerVideo({ id: fileId, contextDispatch }))
      fileUrl = currentVideo.url || ''
    } else {
      storeDispatch(removeComposerImages({ ids: [fileId], contextDispatch }))
      fileUrl = currentImages[fileId]?.url || ''
    }

    const file = currentFiles[fileId] || null
    if (!file) {
      return
    }

    if (file.upload) {
      file.upload.abort(true)
      deleteUpload(fileUrl)
    }
  }, [storeDispatch, contextDispatch, deleteUpload])

  const onFileUploaded = useCallback((url: string, file: File, fileId: string) => {
    const item = { id: fileId, url, isUploading: false, error: undefined, size: file.size }
    if (fileId === latest.current.video?.id) {
      storeDispatch(updateComposerVideo({ item, contextDispatch }))
      return
    }
    storeDispatch(updateComposerImage({ item, contextDispatch }))
  }, [storeDispatch, contextDispatch])

  const onUploadStarted = useCallback((upload: UploadHandle, id: string) => {
    const item = { id, upload, isUploading: true }
    if (id === latest.current.video?.id) {
      storeDispatch(updateComposerVideo({ item, contextDispatch }))
      return
    }
    storeDispatch(updateComposerImage({ item, contextDispatch }))
  }, [storeDispatch, contextDispatch])

  const onUploadError = useCallback((_fileName: string, error: string, uploadId: string) => {
    const item = { id: uploadId, error, isUploading: false }
    if (uploadId === latest.current.video?.id) {
      storeDispatch(updateComposerVideo({ item, contextDispatch }))
      return
    }
    storeDispatch(updateComposerImage({ item, contextDispatch }))
  }, [storeDispatch, contextDispatch])

  const closePhotoEditor = useCallback(() => {
    setPhotoEditorImage(null)
  }, [])

  const onEditFile = useCallback((fileId: string) => {
    const { images: currentImages, files: currentFiles, video: currentVideo } = latest.current
    const file = currentFiles[fileId]
    const isVideo = fileId === currentVideo?.id
    const mediaItem = isVideo ? currentVideo : currentImages[fileId]

    if (!file && !mediaItem) {
      return
    }
    if (isVideo) {
      setVideoEdit({ videoUrl: mediaItem?.url || '', id: fileId, editorOpen: true })
    } else {
      setPhotoEditorImage({ ...file, ...mediaItem })
    }
  }, [])

  const onEditorError = useCallback((error?: Error) => {
    console.log('photo editor error: ', error)
    storeDispatch(message(intl.formatMessage({ id: 'general.media-editor.error' }), 'error'))
    closePhotoEditor()
  }, [storeDispatch, intl, closePhotoEditor])

  const onPhotoEditorExport = useCallback((file: File, id?: string) => {
    if (!id) {
      return
    }

    const imageUrl = latest.current.images[id]?.url
    const upload = latest.current.files[id]?.upload

    // If it's an uploaded file, delete the previous upload
    if (imageUrl && upload) {
      deleteUpload(imageUrl)
    }
    // NOTE: updateComposerImage updates the media context itself — and with `upload: undefined`, which
    // is what the cleanup paths read to tell an upload of ours from a url we do not own. Re-dispatching
    // the handle here put back the one whose url was just deleted above.
    storeDispatch(updateComposerImage({
      item: { id, upload: undefined, url: undefined, file, error: undefined, isEdited: true, size: file?.size },
      contextDispatch
    }))
    closePhotoEditor()
  }, [storeDispatch, contextDispatch, closePhotoEditor, deleteUpload])

  const onVideoEditorExport = useCallback((file: File) => {
    const currentVideo = latest.current.video
    if (currentVideo) {
      const upload = latest.current.files[currentVideo.id]?.upload
      storeDispatch(updateComposerVideo({
        item: { id: currentVideo.id, file, isEdited: true, error: undefined, url: undefined, size: file.size },
        contextDispatch
      }))

      // Only an upload this composer made may be deleted — a video picked from the library or the
      // content drawer shares its url with the item it came from.
      if (upload) {
        deleteUpload(currentVideo.url)
      }
    } else {
      contextDispatch({ type: ACTION_RESET_CONTEXT_FILES })
      // isEdited — the file comes out of the editor, re-validating it as a fresh pick is what the
      // skipValidation flag on the tile exists to prevent.
      storeDispatch(addComposerVideo({ video: { file, isEdited: true }, contextDispatch }))
    }

    setVideoEdit(current => ({ ...current, editorOpen: false }))
  }, [storeDispatch, contextDispatch, deleteUpload])

  const onDragEnd = useCallback((event: DragEndPayload) => {
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const currentOrder = latest.current.order
    const oldIndex = currentOrder.indexOf(active.id)
    const newIndex = currentOrder.indexOf(over.id)
    // NOTE: one action for the whole grid — renumbering image by image dispatched N updates
    storeDispatch(reorderComposerImages(reorder(currentOrder, oldIndex, newIndex)))
  }, [storeDispatch])

  const updateOrientation = useCallback((id: string, orientation: 'landscape' | 'portrait', width: number, height: number) => {
    storeDispatch(updateComposerImage({ item: { id, orientation, width, height }, contextDispatch }))
  }, [storeDispatch, contextDispatch])

  const onFileSizeFetched = useCallback((id: string, size: number) => {
    storeDispatch(updateComposerImage({ item: { id, size }, contextDispatch }))
  }, [storeDispatch, contextDispatch])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  )

  const openVideoEditor = useCallback(() => {
    const currentVideo = latest.current.video
    setVideoEdit({ id: currentVideo?.id, videoUrl: currentVideo?.url || '', editorOpen: true })
  }, [])

  const closeVideoEditor = useCallback(() => {
    setVideoEdit(current => ({ ...current, editorOpen: false }))
  }, [])

  const removeVideo = useCallback((id: string, url: string) => {
    const upload = latest.current.files[id]?.upload
    setVideoEdit({ editorOpen: false, videoUrl: undefined, id: undefined })
    storeDispatch(removeComposerVideo({ id, contextDispatch }))
    // See onVideoEditorExport — a url the composer did not upload is not ours to delete.
    if (upload) {
      deleteUpload(url)
    }
  }, [storeDispatch, contextDispatch, deleteUpload])

  const closeMediaPreviewPopup = useCallback(() => {
    setFilePreview(null)
  }, [])

  const onPreviewVideo = useCallback((url: string, selector: string) => {
    setFilePreview({ url, selector, type: 'video' })
  }, [])

  const onPreviewImage = useCallback((url: string, selector: string) => {
    setFilePreview({ url, selector, type: 'image' })
  }, [])

  return (
    <div className={`${styles.container} ${className || ''}`} data-testid="composer-media-preview">
      {withFiles && (
        <DndContext sensors={sensors} autoScroll={false} onDragEnd={onDragEnd}>
          <SortableContext items={order} strategy={rectSortingStrategy} modifiers={[restrictToParentElement]}>
            <div className={`${styles['files-grid']} ${order.length < 2 ? styles['no-featured'] : ''}`}>
              {order.map(id => {
                if (!images[id]) {
                  return null
                }
                const mediaItem = images[id]
                const file = files[id]

                if (file) {
                  return (
                    <SortableFileGridItem
                      key={id}
                      mediaItem={mediaItem}
                      file={file}
                      isFeatured={mediaItem.order === 1}
                      onRemove={onRemoveFile}
                      onEdit={onEditFile}
                      onFileUploaded={onFileUploaded}
                      onUploadStarted={onUploadStarted}
                      onUploadError={onUploadError}
                      onExpand={setFilePreview}
                      onMetadataLoaded={updateOrientation}
                      onSizeFetched={onFileSizeFetched}
                    />
                  )
                }
                if (mediaItem.url) {
                  return (
                    <ExternalImageGridItem
                      data={mediaItem}
                      key={id}
                      featured={mediaItem.order === 1}
                      onExpand={onPreviewImage}
                      onRemove={onRemoveFile}
                      onEdit={onEditFile}
                      onMetadataLoaded={updateOrientation}
                      onSizeFetched={onFileSizeFetched}
                    />
                  )
                }
                return null
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {video && (
        <div className={styles['files-grid']}>
          <div className={styles['file-box-wrapper']}>
            <div className={`${styles['file-box']} ${styles['video-file']}`}>
              {videoUrl && (
                <UploaderVideoPreview
                  mediaId={video.id}
                  videoUrl={videoUrl}
                  thumbnailUrl={videoThumbnailUrl}
                  onEdit={openVideoEditor}
                  onDelete={removeVideo}
                  onExpand={onPreviewVideo}
                />
              )}
              {!videoUrl && videoFile?.file && (
                <FileGridItem
                  key={video.id}
                  mediaItem={video}
                  file={videoFile}
                  isVideo
                  isFeatured={false}
                  onRemove={onRemoveFile}
                  onEdit={onEditFile}
                  onFileUploaded={onFileUploaded}
                  onUploadStarted={onUploadStarted}
                  onUploadError={onUploadError}
                  onExpand={setFilePreview}
                  onMetadataLoaded={updateOrientation}
                  onSizeFetched={onFileSizeFetched}
                />
              )}
            </div>
            <FileInfo fileUrl={videoUrl} className={styles['file-box-footer']} />
          </div>
        </div>
      )}
      <PhotoEditorDialog
        open={Boolean(photoEditorImage)}
        editId={photoEditorImage?.id || ''}
        image={photoEditorImage?.file || photoEditorImage?.url || ''}
        fileName={photoEditorImage?.file?.name || ''}
        onExport={onPhotoEditorExport}
        onError={onEditorError}
        onClose={closePhotoEditor}
      />
      <VideoEditorDialog
        mediaId={videoEdit.id}
        open={videoEdit.editorOpen}
        video={videoEdit?.videoUrl}
        onExport={onVideoEditorExport}
        onClose={closeVideoEditor}
      />
      {filePreview && (
        <MediaPreviewPopup
          fileUrl={filePreview.url}
          mediaType={filePreview.type}
          elementSelector={filePreview.selector}
          onClose={closeMediaPreviewPopup}
        />
      )}
    </div>
  )
}

export default MediaPreview
